import React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    Select,
    MenuItem,
    IconButton,
    TextField,
    InputAdornment,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteIcon from "@material-ui/icons/Delete";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { modal } from "../../../util";
import PaginationBar from "../../common/PaginationBar";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    voucherCard: {
        marginBottom: theme.spacing(3),
    },
    voucherCode: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
        fontWeight: "bold",
        backgroundColor: "#f5f5f5",
        padding: theme.spacing(0.5),
        borderRadius: theme.spacing(0.5),
        margin: 0,
    },
    statusChip: {
        margin: theme.spacing(0.5),
    },
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    tableHeader: {
        backgroundColor: "#f5f5f5",
    },
    tableCell: {
        padding: theme.spacing(1),
        fontSize: "0.875rem",
    },
    tableHeaderCell: {
        padding: theme.spacing(1),
        fontSize: "0.75rem",
        fontWeight: 600,
    },
    imageLink: {
        color: "#306F9D",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    statusSelect: {
        minWidth: 80,
        height: "32px",
        "& .MuiSelect-select": {
            padding: theme.spacing(0.5, 1),
            fontSize: "0.8125rem",
            paddingRight: theme.spacing(3),
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
        },
    },
    deleteButton: {
        color: theme.palette.error.main,
        "&:disabled": {
            color: theme.palette.action.disabled,
        },
    },
    searchBox: {
        marginBottom: theme.spacing(2),
        maxWidth: 400,
    },
}));

const VoucherDetailPage = () => {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const { setLoading } = useLoading();
    
    const [voucherData, setVoucherData] = React.useState(null);
    const [loading, setLoadingState] = React.useState(true);
    
    // Pagination state for vouchers table
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [paginatedVouchers, setPaginatedVouchers] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState("");

    const fetchVoucherDetails = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingState(true);
            const result = await apiHelper.cineclub.getVoucherById(id);
            console.log('Voucher Details:', result.data);
            
            // Sort vouchers by ID in ascending order
            if (result.data && result.data.vouchers) {
                const sortedVouchers = [...result.data.vouchers].sort((a, b) => {
                    return (a.id || 0) - (b.id || 0);
                });
                setVoucherData({
                    ...result.data,
                    vouchers: sortedVouchers
                });
            } else {
                setVoucherData(result.data);
            }
        } catch (error) {
            console.error('Error fetching voucher details:', error);
        } finally {
            setLoading(false);
            setLoadingState(false);
        }
    }, [id, setLoading]);

    React.useEffect(() => {
        if (id) {
            fetchVoucherDetails();
        }
    }, [fetchVoucherDetails, id]);

    const handleBack = () => {
        history.goBack();
    };

    const formatDate = (dateString) => {
        return dayjs(dateString).format("DD MMM YYYY");
    };

    const getStatusColor = (assigned) => {
        return assigned ? "tertiary" : "primary";
    };

    const getStatusLabel = (assigned) => {
        return assigned ? "Assigned" : "Available";
    };

    const handleStatusChange = async (voucherId, newStatus) => {
        try {
            setLoading(true);
            const response = await apiHelper.cineclub.updateProductVoucher(voucherId, {
                status: newStatus === 1,
                isDeleted: false,
            });
            
            if (response.data && response.data.code === 0) {
                // Reload voucher data to reflect latest stock
                await fetchVoucherDetails();
            } else {
                alert("Failed to update voucher status. Please try again.");
            }
        } catch (error) {
            console.error("Error updating voucher status:", error);
            alert("An error occurred while updating the voucher status. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (voucherId, isAssigned) => {
        // Prevent deletion if voucher is assigned
        if (isAssigned) {
            alert("Cannot delete an assigned voucher. Please unassign it first.");
            return;
        }

        const decision = await modal.fire({
            title: "Delete Voucher",
            text: "Are you sure you want to delete this voucher? This action cannot be undone.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            confirmButtonColor: "#d33",
        });

        if (decision.isConfirmed) {
            try {
                setLoading(true);
                const response = await apiHelper.cineclub.updateProductVoucher(voucherId, {
                    isDeleted: true,
                    status: false,
                });

                if (response.data && response.data.code === 0) {
                    // Reload voucher data to reflect latest stock
                    await fetchVoucherDetails();
                    alert("Voucher deleted successfully.");
                } else {
                    alert("Failed to delete voucher. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting voucher:", error);
                alert("An error occurred while deleting the voucher. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    // Pagination functions
    const gotoPage = React.useCallback((page) => {
        setPageIndex(page);
    }, []);

    const previousPage = React.useCallback(() => {
        setPageIndex(prev => Math.max(0, prev - 1));
    }, []);

    // Filter vouchers based on search term
    const filteredVouchers = React.useMemo(() => {
        if (!voucherData?.vouchers) return [];
        if (!searchTerm.trim()) return voucherData.vouchers;

        const search = searchTerm.toLowerCase().trim();
        return voucherData.vouchers.filter(voucher =>
            voucher.voucherCode?.toLowerCase().includes(search)
        );
    }, [voucherData?.vouchers, searchTerm]);

    // Reset page index when search term changes
    React.useEffect(() => {
        setPageIndex(0);
    }, [searchTerm]);

    const nextPage = React.useCallback(() => {
        const maxPage = Math.max(0, Math.ceil((filteredVouchers?.length || 0) / pageSize) - 1);
        setPageIndex(prev => Math.min(maxPage, prev + 1));
    }, [filteredVouchers?.length, pageSize]);

    const canPreviousPage = pageIndex > 0;
    const canNextPage = pageIndex < Math.ceil((filteredVouchers?.length || 0) / pageSize) - 1;

    // Update paginated vouchers when filtered vouchers or pagination changes
    React.useEffect(() => {
        if (filteredVouchers) {
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const paginated = filteredVouchers.slice(startIndex, endIndex);
            setPaginatedVouchers(paginated);
        }
    }, [filteredVouchers, pageIndex, pageSize]);

    if (loading) {
        return (
            <div className={classes.content}>
                <Typography>Loading voucher details...</Typography>
            </div>
        );
    }

    if (!voucherData) {
        return (
            <div className={classes.content}>
                <Typography color="error">Voucher not found</Typography>
                <Button onClick={handleBack} className={classes.backButton}>
                    <ArrowBackIcon /> Back
                </Button>
            </div>
        );
    }

    return (
        <div className={classes.content}>
            <div className={classes.header}>
                <Button
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    className={classes.backButton}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    Voucher Details
                </Typography>
            </div>

            <Paper className={classes.voucherCard}>
                <Box p={3}>
                    <Typography variant="h5" gutterBottom>
                        {voucherData.title}
                    </Typography>
                    
                    <Grid container spacing={2} style={{ marginBottom: 24 }}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">
                                ID
                            </Typography>
                            <Typography variant="body1">
                                {voucherData.id}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Category
                            </Typography>
                            <Typography variant="body1">
                                {voucherData.category}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Expiry Date
                            </Typography>
                            <Typography variant="body1">
                                {formatDate(voucherData.expiryDate)}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Stock
                            </Typography>
                            <Typography variant="body1">
                                {voucherData.stock}
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Point Price
                            </Typography>
                            <Typography variant="body1">
                                {voucherData.pointPrice || 0}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* <Typography variant="h6" gutterBottom>
                        Voucher Codes ({voucherData.vouchers?.length || 0})
                    </Typography> */}
                    
                    {voucherData.vouchers && voucherData.vouchers.length > 0 ? (
                        <>
                            <Box className={classes.searchBox}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search by voucher code..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {filteredVouchers.length > 0 ? (
                                <>
                            <TableContainer className={classes.tableContainer}>
                                <Table size="small">
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell className={classes.tableHeaderCell}>Voucher Code</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Redeem Date</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Assigned</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Image</TableCell>
                                            <TableCell className={classes.tableHeaderCell}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedVouchers.map((voucher) => (
                                        <TableRow key={voucher.id}>
                                            <TableCell className={classes.tableCell}>
                                                <Typography 
                                                    className={classes.voucherCode}
                                                    variant="body2"
                                                    style={{ fontSize: "0.875rem" }}
                                                >
                                                    {voucher.voucherCode}
                                                </Typography>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {voucher.redeemDate ? formatDate(voucher.redeemDate) : '-'}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Chip
                                                    label={getStatusLabel(voucher.assigned)}
                                                    color={getStatusColor(voucher.assigned)}
                                                    size="small"
                                                    className={classes.statusChip}
                                                />
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <Select
                                                    value={voucher.status ? 1 : 0}
                                                    onChange={(e) => handleStatusChange(voucher.id, e.target.value)}
                                                    className={classes.statusSelect}
                                                    variant="outlined"
                                                    size="small"
                                                >
                                                    <MenuItem value={1}>Active</MenuItem>
                                                    <MenuItem value={0}>Inactive</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                {voucher.imagePath ? (
                                                    <Link
                                                        href={voucher.imagePath}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classes.imageLink}
                                                        style={{ fontSize: "0.875rem" }}
                                                    >
                                                        View Image
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.875rem" }}>
                                                        No image
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell className={classes.tableCell}>
                                                <IconButton
                                                    onClick={() => handleDelete(voucher.id, voucher.assigned)}
                                                    className={classes.deleteButton}
                                                    size="small"
                                                    aria-label="delete"
                                                    disabled={voucher.assigned}
                                                    title={voucher.assigned ? "Cannot delete assigned voucher" : "Delete voucher"}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {/* Pagination Bar */}
                        {filteredVouchers.length > pageSize && (
                            <PaginationBar
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                gotoPage={gotoPage}
                                previousPage={previousPage}
                                nextPage={nextPage}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                pageIndex={pageIndex}
                                totalCount={filteredVouchers.length}
                            />
                        )}
                                </>
                            ) : (
                                <Typography color="textSecondary" style={{ marginTop: 16 }}>
                                    No vouchers found matching "{searchTerm}"
                                </Typography>
                            )}
                    </>
                    ) : (
                        <Typography color="textSecondary">
                            No voucher codes available
                        </Typography>
                    )}
                </Box>
            </Paper>
        </div>
    );
};

export default VoucherDetailPage;
